import {
  CONTRACT_TYPE_LABELS,
  DO_CONTRACT_TYPES,
} from "@etudescarre/constants";
import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import { DocumentTable } from "../components/document-table/document-table";
import type { DocumentData, LotData } from "../components/document-table/types";
import { PdfViewer, type PdfViewerFile } from "../components/pdf-viewer";
import { InterventionsAVenirDo } from "./interventions-a-venir-do";

type DateSummary = { date: string | null; interventionCount: number };
type FilterOption = { id: number; name: string };
type DatesData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
  };
  dates: DateSummary[];
};
type DateIntervention = {
  id: number;
  dateIntervention: string | null;
  building: { id: number; name: string; shortName: string | null };
  zone: { id: number; name: string; shortName: string | null };
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  providerContracts: Array<{
    id: number;
    label: string;
    companyName: string;
    type: "travaux_n_1" | "travaux_n_2";
  }>;
  forecastDocumentCount: number;
  producedDocumentCount: number;
  latestBpeDocumentCount: number;
  latestFavorableDocumentCount: number;
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: DatesData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; interventions: DateIntervention[] };

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

const formatDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export function InterventionsAVenir() {
  const { hasContractType } = useYardContext();
  // Écran 027 : vue des contrats de maîtrise (AMO / CT / SPS / MOE).
  if (DO_CONTRACT_TYPES.some((type) => hasContractType(type))) {
    return <InterventionsAVenirDo />;
  }
  // Écran 014 (conducteur / directeur de travaux).
  return <InterventionsAVenirTravaux />;
}

function InterventionsAVenirTravaux() {
  const { yardId } = useParams<{ yardId: string }>();
  const { contracts } = useYardContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  // "none" targets the undated interventions; null = no selection yet.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [pdfFile, setPdfFile] = useState<PdfViewerFile | null>(null);
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const lots = parseIds(lotQuery);
  const performances = parseIds(performanceQuery);
  const piloted = searchParams.get("piloted") === "true";
  const recentOnly = searchParams.get("recentOnly") === "true";

  useEffect(() => {
    if (isNil(yardId)) return;
    let cancelled = false;
    setState({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/dates", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            ...(piloted ? { piloted: "true" as const } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        const first = data.data.dates[0];
        setSelectedDate((current) => {
          if (
            isNotNil(current) &&
            data.data.dates.some((item) => (item.date ?? "none") === current)
          ) {
            return current;
          }
          return isNotNil(first) ? (first.date ?? "none") : null;
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery, piloted]);

  useEffect(() => {
    void detailRefresh;
    if (isNil(yardId) || isNil(selectedDate)) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/dates/{date}", {
        params: {
          path: { yardId, date: selectedDate },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            ...(piloted ? { piloted: "true" as const } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [
    yardId,
    selectedDate,
    lotQuery,
    performanceQuery,
    piloted,
    recentOnly,
    detailRefresh,
  ]);

  const updateFilter = (key: "lot" | "performance", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setBooleanParam = (key: "piloted" | "recentOnly", checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set(key, "true");
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const viewPdf = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (isNotNil(data)) setPdfFile(data.data);
    },
    [yardId],
  );
  const downloadNative = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (isNotNil(data))
        window.open(data.data.url, "_blank", "noopener,noreferrer");
    },
    [yardId],
  );
  const markBpe = useCallback(
    async (versionId: number, documentId: number) => {
      if (isNil(yardId)) return;
      const confirmed = await confirmDialog({
        title: "Marquer BPE",
        message: "Marquer cette version comme BPE ?",
        confirmLabel: "Marquer BPE",
      });
      if (!confirmed) return;
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/bpe",
        {
          params: {
            path: {
              yardId,
              documentId: String(documentId),
              versionId: String(versionId),
            },
          },
        },
      );
      if (isNotNil(error))
        toaster.error({
          title: "Version non marquée BPE",
          description: error.error,
        });
      else {
        toaster.success({ title: "Modifications enregistrées" });
        setDetailRefresh((value) => value + 1);
      }
    },
    [yardId],
  );
  const canMarkBpe = contracts.some(
    (contractItem) =>
      contractItem.type === "travaux_n_1" ||
      contractItem.type === "travaux_n_2",
  );

  if (state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage text="Impossible de charger les interventions." error />
    );

  return (
    <div className="flex h-full flex-col gap-5 overflow-hidden p-4 lg:p-6">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Interventions à venir
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez les interventions du chantier par date de démarrage.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={lots.map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={performances.map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={piloted}
            onChange={(event) =>
              setBooleanParam("piloted", event.target.checked)
            }
          />
          Interventions que je pilote
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) =>
              setBooleanParam("recentOnly", event.target.checked)
            }
          />
          Indices récents uniquement
        </label>
      </section>
      {state.data.dates.length === 0 ? (
        <PageMessage text="Aucune intervention ne correspond aux filtres." />
      ) : (
        <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto lg:grid-cols-[260px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
          <nav
            aria-label="Dates des interventions"
            className="space-y-1 lg:overflow-y-auto"
          >
            {state.data.dates.map((item) => {
              const key = item.date ?? "none";
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDate(key)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm",
                    key === selectedDate
                      ? "bg-blue-600 text-white"
                      : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100",
                  )}
                >
                  <span>
                    {isNotNil(item.date) ? formatDate(item.date) : "Sans date"}
                  </span>
                  <span className="text-xs opacity-80">
                    {item.interventionCount}
                  </span>
                </button>
              );
            })}
          </nav>
          <main className="min-w-0 space-y-3 lg:overflow-y-auto">
            <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
              {isNotNil(selectedDate) && selectedDate !== "none"
                ? formatDate(selectedDate)
                : "Interventions sans date"}
            </h3>
            <DateDetail
              state={detail}
              canMarkBpe={canMarkBpe}
              onViewPdf={viewPdf}
              onDownloadNative={downloadNative}
              onMarkBpe={markBpe}
            />
          </main>
        </div>
      )}
      {isNotNil(pdfFile) && (
        <PdfViewer file={pdfFile} onClose={() => setPdfFile(null)} />
      )}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}

type DateDetailProps = {
  state: DetailState;
  canMarkBpe: boolean;
  onViewPdf: (id: number) => void;
  onDownloadNative: (id: number) => void;
  onMarkBpe: (versionId: number, documentId: number) => void;
};
function DateDetail({
  state,
  canMarkBpe,
  onViewPdf,
  onDownloadNative,
  onMarkBpe,
}: DateDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette date."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => (
        <InterventionCard
          key={item.id}
          intervention={item}
          canMarkBpe={canMarkBpe}
          onViewPdf={onViewPdf}
          onDownloadNative={onDownloadNative}
          onMarkBpe={onMarkBpe}
        />
      ))}
    </div>
  );
}

type InterventionCardProps = {
  intervention: DateIntervention;
  canMarkBpe: boolean;
  onViewPdf: (id: number) => void;
  onDownloadNative: (id: number) => void;
  onMarkBpe: (versionId: number, documentId: number) => void;
};
function InterventionCard({
  intervention,
  canMarkBpe,
  onViewPdf,
  onDownloadNative,
  onMarkBpe,
}: InterventionCardProps) {
  const [open, setOpen] = useState(false);
  const lots: LotData[] = [
    {
      id: intervention.lot.id,
      name: intervention.lot.name,
      nLot: intervention.lot.nLot,
      prestas: [
        {
          id: intervention.performance.id,
          name: intervention.performance.name,
          documents: intervention.documents,
        },
      ],
    },
  ];
  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold text-zinc-900 dark:text-zinc-100">
            {intervention.building.shortName ?? intervention.building.name} ·{" "}
            {intervention.zone.name} ·{" "}
            {intervention.lot.nLot ? `Lot ${intervention.lot.nLot} — ` : ""}
            {intervention.performance.name}
          </h4>
          {intervention.providerContracts.length === 0 ? (
            <p className="mt-1 text-sm text-zinc-500">
              Aucun contrat prestataire actif
            </p>
          ) : (
            intervention.providerContracts.map((provider) => (
              <p key={provider.id} className="mt-1 text-sm text-zinc-500">
                {`${CONTRACT_TYPE_LABELS[provider.type]} : ${provider.label} (${provider.companyName})`}
              </p>
            ))
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-zinc-600 md:grid-cols-4 dark:text-zinc-400">
        <span>
          {intervention.forecastDocumentCount} document(s) prévisionnel(s)
        </span>
        <span>{intervention.producedDocumentCount} document(s) produit(s)</span>
        <span>
          {intervention.latestBpeDocumentCount} dernière(s) version(s) BPE
        </span>
        <span>
          {intervention.latestFavorableDocumentCount} dernière(s) version(s)
          visée(s) favorablement
        </span>
      </div>
      <div className="mt-4">
        {intervention.documents.length === 0 ? (
          <p className="rounded-lg bg-zinc-50 px-4 py-3 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
            Aucun document accessible pour cette intervention.
          </p>
        ) : (
          <>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
              className="flex items-center gap-1 text-sm font-medium text-blue-700 dark:text-blue-400"
            >
              {open ? <CaretDownIcon /> : <CaretRightIcon />}
              Détail des documents
            </button>
            {open && (
              <div className="mt-3">
                <DocumentTable
                  lots={lots}
                  canMarkBpe={canMarkBpe}
                  onViewPdf={onViewPdf}
                  onDownloadNative={onDownloadNative}
                  onMarkBpe={onMarkBpe}
                  onOpenSynthese={() => undefined}
                  onOpenDestinataires={() => undefined}
                  onOpenInterne={() => undefined}
                  onOpenDiffusion={() => undefined}
                  showDiffusionActions={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}
